import React, { useCallback, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Home, RotateCcw, Share2, Sparkles } from 'lucide-react';

const revealBall = keyframes`
  0% {
    opacity: 0;
    transform: translateY(18px) scale(0.72) rotate(-18deg);
  }
  70% {
    opacity: 1;
    transform: translateY(-6px) scale(1.08) rotate(5deg);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0);
  }
`;

const leverPull = keyframes`
  0% { transform: rotate(-18deg); }
  45% { transform: rotate(28deg); }
  100% { transform: rotate(-18deg); }
`;

const PageShell = styled.main`
  width: min(100%, 760px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  color: #24212f;
`;

const ToolPanel = styled.section`
  width: 100%;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(100, 84, 148, 0.18);
  border-radius: 8px;
  box-shadow: 0 18px 50px rgba(57, 45, 97, 0.16);
  box-sizing: border-box;
  padding: 28px;
  backdrop-filter: blur(7px);

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 18px;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #6d5ca5;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const Heading = styled.h1`
  margin: 0;
  color: #24212f;
  font-size: 34px;
  line-height: 1.18;
  letter-spacing: 0;

  @media (max-width: 640px) {
    font-size: 28px;
  }
`;

const SubText = styled.p`
  margin: 8px 0 0;
  color: #6b657a;
  font-size: 16px;
  line-height: 1.55;
`;

const HomeLink = styled(Link)`
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(100, 84, 148, 0.2);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #645494;
  background: #fff;
  text-decoration: none;
  transition: transform 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    color: #33284f;
    border-color: rgba(100, 84, 148, 0.48);
    transform: translateY(-2px);
  }
`;

const Machine = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 142px;
  gap: 22px;
  align-items: stretch;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const NumberDeck = styled.div`
  min-height: 218px;
  border: 1px solid rgba(100, 84, 148, 0.18);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(255, 250, 237, 0.8), rgba(255, 255, 255, 0.96)),
    repeating-linear-gradient(90deg, rgba(100, 84, 148, 0.07) 0 1px, transparent 1px 34px);
  padding: 22px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
`;

const Balls = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(54px, 1fr));
  gap: 12px;

  @media (max-width: 620px) {
    grid-template-columns: repeat(3, minmax(64px, 1fr));
  }
`;

const Ball = styled.div`
  aspect-ratio: 1;
  min-width: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
  box-shadow:
    inset 0 -8px 14px rgba(0, 0, 0, 0.16),
    inset 0 8px 12px rgba(255, 255, 255, 0.26),
    0 9px 20px rgba(36, 33, 47, 0.16);
  background: ${props => getBallColor(props.$number)};
  animation: ${revealBall} 0.54s cubic-bezier(.2,.85,.28,1.25) both;
  animation-delay: ${props => props.$index * 0.08}s;
`;

const EmptySlot = styled.div`
  aspect-ratio: 1;
  min-width: 0;
  border-radius: 50%;
  border: 2px dashed rgba(100, 84, 148, 0.28);
  background: rgba(255, 255, 255, 0.62);
`;

const Message = styled.div`
  min-height: 30px;
  color: ${props => props.$tone === 'error' ? '#b52b45' : '#4f456e'};
  font-size: 15px;
  font-weight: 700;
  text-align: center;
`;

const LeverButton = styled.button`
  position: relative;
  border: 0;
  border-radius: 8px;
  min-height: 218px;
  padding: 18px 16px;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(180deg, #3c3459, #211c33);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.12),
    0 16px 34px rgba(36, 33, 47, 0.22);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      inset 0 0 0 1px rgba(255, 255, 255, 0.18),
      0 20px 40px rgba(36, 33, 47, 0.27);
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 720px) {
    min-height: 120px;
  }
`;

const LeverRail = styled.span`
  position: absolute;
  top: 26px;
  left: 50%;
  width: 16px;
  height: 94px;
  transform: translateX(-50%);
  transform-origin: bottom center;
  border-radius: 999px;
  background: linear-gradient(180deg, #efe9ff, #9385c6);
  box-shadow: 0 9px 18px rgba(0, 0, 0, 0.26);
  animation: ${props => props.$active ? leverPull : 'none'} 0.58s ease;

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: 50%;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    transform: translateX(-50%);
    background: radial-gradient(circle at 35% 30%, #fff6aa, #f4bd37 42%, #c8781f 100%);
    box-shadow: 0 8px 20px rgba(244, 189, 55, 0.38);
  }

  @media (max-width: 720px) {
    top: 18px;
    left: 34px;
    height: 68px;
  }
`;

const LeverBase = styled.span`
  position: absolute;
  left: 50%;
  top: 118px;
  width: 72px;
  height: 28px;
  transform: translateX(-50%);
  border-radius: 999px;
  background: #151222;
  box-shadow: inset 0 8px 12px rgba(255, 255, 255, 0.08);

  @media (max-width: 720px) {
    top: 76px;
    left: 34px;
    width: 58px;
    height: 22px;
  }
`;

const LeverText = styled.span`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 800;

  @media (max-width: 720px) {
    left: 92px;
    right: 16px;
    top: 0;
    bottom: 0;
    font-size: 18px;
  }
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 18px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ActionButton = styled.button`
  min-height: 46px;
  border: 0;
  border-radius: 8px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #fff;
  background: ${props => props.$variant === 'secondary' ? '#645494' : '#e2684f'};
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s ease, filter 0.2s ease, opacity 0.2s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(0.97);
  }
`;

const MiniStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 18px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  border: 1px solid rgba(100, 84, 148, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  padding: 12px;
  text-align: center;
`;

const StatLabel = styled.div`
  color: #7b728d;
  font-size: 13px;
  font-weight: 700;
`;

const StatValue = styled.div`
  margin-top: 4px;
  color: #2e2842;
  font-size: 20px;
  font-weight: 900;
`;

const Notice = styled.p`
  width: min(100%, 700px);
  margin: 0;
  color: rgba(68, 58, 96, 0.74);
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
`;

function getBallColor(number) {
  if (number <= 10) return 'linear-gradient(145deg, #f2be37, #d88a1f)';
  if (number <= 20) return 'linear-gradient(145deg, #4db8d7, #2b78aa)';
  if (number <= 30) return 'linear-gradient(145deg, #ef6e68, #bf3f50)';
  if (number <= 40) return 'linear-gradient(145deg, #8f96a3, #626b77)';
  return 'linear-gradient(145deg, #76bd65, #4d8f4e)';
}

function generateNumbers() {
  const pool = Array.from({ length: 45 }, (_, index) => index + 1);

  for (let index = pool.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [pool[index], pool[randomIndex]] = [pool[randomIndex], pool[index]];
  }

  return pool.slice(0, 6).sort((a, b) => a - b);
}

function parseNumbers(value) {
  if (!value) return null;

  const numbers = value
    .split('-')
    .map(part => Number(part))
    .filter(number => Number.isInteger(number));
  const uniqueNumbers = new Set(numbers);
  const validNumbers = numbers.every(number => number >= 1 && number <= 45);

  if (numbers.length !== 6 || uniqueNumbers.size !== 6 || !validNumbers) {
    return null;
  }

  return [...numbers].sort((a, b) => a - b);
}

function Lotto() {
  const { numbers: sharedNumbers } = useParams();
  const navigate = useNavigate();
  const parsedNumbers = useMemo(() => parseNumbers(sharedNumbers), [sharedNumbers]);
  const isSharePage = Boolean(sharedNumbers);
  const [numbers, setNumbers] = useState(parsedNumbers || []);
  const [leverRun, setLeverRun] = useState(0);
  const [copied, setCopied] = useState(false);

  const visibleNumbers = parsedNumbers || numbers;
  const hasNumbers = visibleNumbers.length === 6;
  const numberPath = hasNumbers ? visibleNumbers.join('-') : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = hasNumbers ? `${origin}/lotto/${numberPath}` : '';
  const sum = hasNumbers ? visibleNumbers.reduce((acc, number) => acc + number, 0) : 0;
  const oddCount = hasNumbers ? visibleNumbers.filter(number => number % 2 === 1).length : 0;
  const hasInvalidSharePath = isSharePage && !parsedNumbers && !hasNumbers;

  const handleGenerate = useCallback(() => {
    setNumbers(generateNumbers());
    setLeverRun(value => value + 1);
    setCopied(false);
  }, []);

  const handleShare = useCallback(() => {
    if (!hasNumbers) return;
    navigate(`/lotto/${numberPath}`);
  }, [hasNumbers, navigate, numberPath]);

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      setCopied(false);
      window.prompt('공유 링크', shareUrl);
    }
  }, [shareUrl]);

  const handleReset = useCallback(() => {
    setNumbers([]);
    setCopied(false);
    navigate('/lotto');
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>몽픽 로또 번호 생성기 - 1부터 45까지 랜덤 번호 만들기</title>
        <meta name="description" content="몽픽 로또 번호 생성기에서 1부터 45까지의 숫자 중 6개를 간단히 뽑고 공유 링크로 저장해보세요." />
        <link rel="canonical" href="https://mongpick.com/lotto" />
        <meta property="og:title" content="몽픽 로또 번호 생성기" />
        <meta property="og:description" content="레버를 당기듯 1부터 45까지의 숫자 중 6개를 뽑아 공유할 수 있는 몽픽 로또 번호 생성기입니다." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mongpick.com/lotto" />
      </Helmet>
      <PageShell>
        <ToolPanel>
        <Header>
          <div>
            <Eyebrow>
              <Sparkles size={16} />
              MongPick Lucky Draw
            </Eyebrow>
            <Heading>{isSharePage ? '오늘의 몽픽 번호' : '몽픽 로또 번호 생성기'}</Heading>
            <SubText>
              {isSharePage
                ? '꿈처럼 남겨진 여섯 숫자입니다.'
                : '레버를 당기면 1부터 45까지의 숫자 중 여섯 개가 나타납니다.'}
            </SubText>
          </div>
          <HomeLink to="/" aria-label="몽픽 홈">
            <Home size={20} />
          </HomeLink>
        </Header>

        <Machine>
          <NumberDeck>
            <Balls aria-label="로또 번호">
              {hasNumbers
                ? visibleNumbers.map((number, index) => (
                    <Ball key={`${number}-${leverRun}`} $number={number} $index={index}>
                      {number}
                    </Ball>
                  ))
                : Array.from({ length: 6 }, (_, index) => <EmptySlot key={index} />)}
            </Balls>
            <Message $tone={hasInvalidSharePath ? 'error' : 'normal'}>
              {hasInvalidSharePath
                ? '유효한 번호가 아닙니다.'
                : hasNumbers
                  ? copied
                    ? '공유 링크를 복사했습니다.'
                    : '좋은 흐름이 머물기를 바랍니다.'
                  : '몽픽의 행운 레버가 기다리고 있습니다.'}
            </Message>
          </NumberDeck>

          <LeverButton type="button" onClick={handleGenerate} aria-label="번호 생성">
            <LeverRail key={leverRun} $active={leverRun > 0} />
            <LeverBase />
            <LeverText>
              <Sparkles size={18} />
              Pick
            </LeverText>
          </LeverButton>
        </Machine>

        <MiniStats>
          <Stat>
            <StatLabel>합계</StatLabel>
            <StatValue>{hasNumbers ? sum : '-'}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>홀짝</StatLabel>
            <StatValue>{hasNumbers ? `${oddCount}:${6 - oddCount}` : '-'}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>범위</StatLabel>
            <StatValue>{hasNumbers ? `${visibleNumbers[0]}-${visibleNumbers[5]}` : '-'}</StatValue>
          </Stat>
        </MiniStats>

        <Actions>
          {isSharePage ? (
            <>
              <ActionButton type="button" onClick={handleReset} $variant="secondary">
                <RotateCcw size={18} />
                새로 뽑기
              </ActionButton>
              <ActionButton type="button" onClick={handleCopy} disabled={!hasNumbers}>
                <Share2 size={18} />
                링크 복사
              </ActionButton>
            </>
          ) : (
            <>
              <ActionButton type="button" onClick={handleGenerate} $variant="secondary">
                <RotateCcw size={18} />
                다시 뽑기
              </ActionButton>
              <ActionButton type="button" onClick={handleShare} disabled={!hasNumbers}>
                <Share2 size={18} />
                공유하기
              </ActionButton>
            </>
          )}
        </Actions>
      </ToolPanel>

        <Notice>번호 생성은 재미용이며, 구매와 당첨을 보장하지 않습니다.</Notice>
      </PageShell>
    </>
  );
}

export default Lotto;
